import type { ResultOf } from "@graphql-typed-document-node/core";
import { evalCharges } from "@npco/web-utils";
import { Toaster } from "@npco/zeller-design-system";
import { ADT } from "pb.adt";
import { safe } from "pb.safe";
import type { Disposer, Mutable } from "pb.types";
import { useEffect, useState } from "react";

import {
	$,
	ContactMethod,
	EcommerceStatus,
	EcommerceTransactionStatus,
	query,
	subscription,
	TransactionStatus,
} from "../../../../schema.dev-sdk-api-appsync.generated";
import { logger } from "../../../shared/logger";
import { never } from "../../../shared/never";
import * as transport from "../../../transport";
import type { Route } from "../shared/route";
import { useApiClient } from "../shared/use-api-client";
import { useHealthcheck } from "../shared/use-healthcheck";
import { useWsClient } from "../shared/use-ws-client";
import {
	getVirtualTerminalContext,
	type VirtualTerminalContext,
} from "./purchase-with-virtual-terminal/get-virtual-terminal-context";
import { Page } from "./purchase-with-virtual-terminal/page";
import { render } from "./purchase-with-virtual-terminal/render";
import { response_fromTransactionResponse } from "./purchase-with-virtual-terminal/response-from-transaction-response";
import { sendPayByLink } from "./purchase-with-virtual-terminal/send-pay-by-link";
import { voidPayByLink } from "./purchase-with-virtual-terminal/void-pay-by-link";

const resolve = transport.Resolve<"Terminal_purchase">();

const initialPage = Page.Initialising({
	onClose: () => void resolve.Error("Cancelled"),
});

type Context = VirtualTerminalContext & {
	total: number;
	subtotal: number;
	surcharge: number;
};

export namespace PurchaseWithVirtualTerminal {
	export type Props = {
		request: Route.Request<"Terminal_purchase">;
	};
}

export function PurchaseWithVirtualTerminal(
	props: PurchaseWithVirtualTerminal.Props,
) {
	const { request } = props;

	const wsClient = useWsClient();
	const apiClient = useApiClient();
	const healthcheck = useHealthcheck();
	const toaster = Toaster.useToaster();

	const [page, _setPage] = useState<Page>(initialPage);

	useEffect(() => {
		let $dispose: Disposer | undefined = () => undefined;
		function dispose() {
			$dispose?.();
		}

		function done(page: Page | undefined) {
			// Stop all timeouts and subscriptions.
			dispose();
			// Ignore future external cancels.
			transport.onCancel(() => undefined);

			if (done.isLocked) {
				return;
			} else {
				done.isLocked = true;
			}

			if (page) {
				setPage(page);
			}
		}
		done.isLocked = false;

		if (!request.headers.deviceId) {
			return void done(
				Page.ErrorSetupRequired({
					onClose: () => void resolve.Error("Setup Required"),
				}),
			);
		}

		const { deviceId } = request.headers;

		main();
		async function main() {
			const onRetry = () => {
				// Unlock on retry.
				done.isLocked = false;
				main();
			};

			setPage(initialPage);

			// Default cancel behaviour, show cancelled page, exit with "Cancelled".
			transport.onCancel(
				() =>
					void done(
						Page.PaymentCancelled({
							onClose: () => void resolve.Error("Cancelled"),
						}),
					),
			);

			const result = await getVirtualTerminalContext({ apiClient, deviceId });
			if (done.isLocked) {
				return;
			}

			if (result instanceof Error) {
				if (result.type === "Setup") {
					return void done(
						Page.ErrorSetupRequired({
							onClose: () => void resolve.Error("Setup Required"),
						}),
					);
				}
				return void done(
					Page.ErrorNetworkFailure({
						onClose: () => void resolve.Error("Network Failure"),
						onRetry,
					}),
				);
			}

			const { totalAmount, surchargeAmount } = evalCharges({
				amount: request.query.amount,
				surcharge: result.surchargesTaxes?.feesSurchargePbl,
				// All tax-related options are excluded/null for SDK initiated
				// purchases and are instead delegated to the integrators' POS system
				// to calculate and itemise for their own tax invoices and receipts.
				taxEnabled: null,
				taxPercentBasisPoints: null,
				consumptionTax: null,
			});

			const context: Context = {
				...result,
				total: totalAmount,
				subtotal: request.query.amount,
				surcharge: surchargeAmount ?? 0,
			};

			setPage(Page_Selection(context));
		}

		function Page_Selection(context: Context) {
			return Page.PaymentMethodSelection({
				onClose: () => void resolve.Error("Cancelled"),
				onLinkEmail: () => setPage(Page_PayByLinkForm(context, "email")),
				onLinkSMS: () => setPage(Page_PayByLinkForm(context, "sms")),
			});
		}

		function Page_PayByLinkForm(context: Context, method: "email" | "sms") {
			// Default to Email
			return (
				method === "sms"
					? Page.PaymentMethodLinkSMS
					: Page.PaymentMethodLinkEmail
			)({
				total: context.total,
				surcharge: context.surcharge,
				currency: context.currency,
				onClose: () => void resolve.Error("Cancelled"),
				onSubmit: async (handle) => {
					const result = await sendPayByLink({
						apiClient,
						entityId: context.entityId,
						deviceId: context.deviceId,
						amount: context.subtotal,
						// Default to Email
						targetMethod:
							method === "sms" ? ContactMethod.SMS : ContactMethod.EMAIL,
						targetHandle: handle,
						reference: request.query.reference,
					});

					if (done.isLocked) {
						return;
					}

					if (result instanceof Error) {
						return void toaster.toast({
							message: "Failed to submit.",
							status: "negative",
						});
					}

					Flow_PayByLinkListen(
						{
							id: result.createAndSendPayByLink.id,
							reference: result.createAndSendPayByLink.reference,
						},
						context,
					);
				},
				onBack: () => void setPage(Page_Selection(context)),
			});
		}

		async function Flow_PayByLinkListen(
			ecommerce: { id: string; reference: string },
			context: Context,
		) {
			const expiry_s = Date.now() / 1000 + 15 * 60 + 15; /* Now + 15m + 15s */

			// We shouldn't lose this value between flow attempts.
			let $viewedTimestamp: string | undefined = undefined;

			flow();
			function flow() {
				const onRetry = () => {
					// Unlock on retry.
					done.isLocked = false;
					flow();
				};

				// Cancellation.
				transport.onCancel(onCancel);

				let $expiry_timeout: ReturnType<typeof setTimeout> | undefined =
					undefined;
				let $shared_timeout: ReturnType<typeof setTimeout> | undefined =
					undefined;
				let $subscription:
					| Exclude<Awaited<ReturnType<typeof wsClient.subscribe>>, Error>
					| undefined = undefined;

				$dispose = () => {
					$expiry_timeout = void clearTimeout($expiry_timeout);
					$shared_timeout = void clearTimeout($shared_timeout);
					$subscription = void $subscription?.close();

					// Cleanup itself.
					$dispose = undefined;
				};

				function voidRecord() {
					return voidPayByLink({
						apiClient,
						entityId: context.entityId,
						deviceId: context.deviceId,
						ecommerceId: ecommerce.id,
					});
				}

				async function onCancel() {
					// Close everything, but we have an intermediate cancelling state.
					done(undefined);

					// Cancelling pending.
					setPage((page) => ({ ...page, cancelling: true }));

					// Done page.
					const page = Page.PaymentCancelled({
						onClose: () => void resolve.Error("Cancelled"),
					});

					// Prevent hanging requests blocking UI cancellation.
					$shared_timeout =
						(void clearTimeout($shared_timeout),
						setTimeout(() => {
							setPage(page);
							$shared_timeout = undefined;
						}, 5_000 /* 5s */));

					// Void the e-commerce record.
					await voidRecord();

					// Timeout hasn't been called yet.
					if ($shared_timeout) {
						$shared_timeout = void clearTimeout($shared_timeout);
						setPage(page);
					}
				}

				function onTimeout() {
					done(
						Page.PaymentExpired({
							// Proposed: { type: "Link Expired" }
							onClose: () => void resolve.Error("Inactivity Timeout"),
						}),
					);

					// Cleanup just in case. We don't care if it fails.
					voidRecord();
				}

				const expiryDuration_s = expiry_s - Date.now() / 1000;

				// Show expiry/timeout if retried after expiry.
				if (expiryDuration_s < 0) {
					return void onTimeout();
				}

				// Initial empty flow state.
				setPage(
					Page.PaymentFlow({
						state: ADT.value("Idle", {
							onCancel: onCancel,
							timeoutDuration: expiryDuration_s,
						}),
						transactions: [],
						ecommerceReference: ecommerce.reference,
					}),
				);

				// Expiration timeout.
				$expiry_timeout =
					(void clearTimeout($expiry_timeout),
					setTimeout(onTimeout, expiryDuration_s * 1000));

				/**
				 * `1` is used when reseting between reconnection attempts.
				 * `5` is last/final attempt.
				 */
				let $attempt = 1;

				connect();
				async function connect() {
					logger.debug("purchase: connect", {
						attempt: $attempt,
					});

					const onClose = (callback: () => void) => async () => {
						// Gracefully await with loading spinner in Close button.
						voidRecord();
						callback();
					};

					const subscription =
						// Ensure previous subscription is closed and disposed before next.
						(($subscription = void $subscription?.close()),
						await wsClient.subscribe({
							query: PurchaseWithVirtualTerminal.onVirtualTerminalRecordUpdate,
							variables: {
								entityId: context.entityId,
								ecommerceId: ecommerce.id,
							},
						}));

					const NetworkFailure_page = Page.ErrorNetworkFailure({
						onClose: onClose(() => void resolve.Error("Network Failure")),
						onRetry,
					});

					// If authentication/token failure.
					if (subscription instanceof Error) {
						logger.error("purchase: subscription error", {
							type: subscription.type,
							message: subscription.message,
						});

						switch (subscription.type) {
							case "TokenFailure":
								return void done(
									Page.ErrorSetupRequired({
										onClose: onClose(
											() => void resolve.Error("Setup Required"),
										),
									}),
								);
							case "InitTimeout":
								return void done(NetworkFailure_page);
							case "InitFailure":
								return void done(
									Page.ErrorOperationFailure({
										onClose: onClose(
											() => void resolve.Error("Operation Failure"),
										),
										onRetry,
									}),
								);
							default:
								return never(subscription);
						}
					}

					// If interrupted, do not continue and cleanup subscription.
					if (done.isLocked) {
						return void subscription.close();
					}

					// Assign as established subscription.
					$subscription = subscription;

					subscription.onDisconnect = () => {
						logger.error("purchase: subscription disconnected", {
							attempt: $attempt,
						});

						// Reconnect...

						/* v8 ignore next 3 -- final reconnect attempt; reaching it requires driving five sequential disconnect/handshake cycles, which the websocket harness cannot reliably reproduce */
						if ($attempt >= 5) {
							return void done(NetworkFailure_page);
						}

						$shared_timeout =
							(void clearTimeout($shared_timeout),
							setTimeout(async () => {
								// checks api resolution; retries with refreshed token on 401.
								// success permits web socket reconnect, failure signals network
								// error.
								const result = await healthcheck(deviceId);

								if (result instanceof Error) {
									return void done(NetworkFailure_page);
								}

								$attempt += 1;
								connect();
							}, 1000 * $attempt));
					};

					subscription.onData = (data) => {
						(async () => {
							const status = data.onVirtualTerminalRecordUpdate?.status;

							if (status === EcommerceStatus.CANCELLED) {
								return void done(
									Page.PaymentCancelled({
										onClose: onClose(() => void resolve.Error("Cancelled")),
									}),
								);
							}

							if (status === EcommerceStatus.EXPIRED) {
								return void done(
									Page.PaymentExpired({
										onClose: onClose(
											() => void resolve.Error("Inactivity Timeout"),
										),
									}),
								);
							}

							if (!$viewedTimestamp) {
								$viewedTimestamp =
									data.onVirtualTerminalRecordUpdate?.lastViewedAt || undefined;
							}

							type TransactionId = string;
							type TransactionResponse = ResultOf<
								typeof PurchaseWithVirtualTerminal.getTransaction
							>["getTransaction"];
							const transactionResponses_byId = new Map<
								TransactionId,
								TransactionResponse
							>();

							let isProcessing = false;
							const requests: Array<Promise<void>> = [];
							data.onVirtualTerminalRecordUpdate?.transactionRecords?.forEach(
								(transactionRecord) => {
									if (!transactionRecord) {
										return;
									}

									if (
										transactionRecord.status ===
										EcommerceTransactionStatus.PROCESSING
									) {
										isProcessing = true;
										return;
									}

									requests.push(
										(async () => {
											const transactionId = transactionRecord.transactionUuid;
											const result = await safe(() =>
												apiClient.request(
													PurchaseWithVirtualTerminal.getTransaction,
													{
														deviceId: context.deviceId,
														transactionId,
													},
												),
											);

											if (result instanceof Error) {
												// Show toast. Consider better error handling.
												return;
											}

											transactionResponses_byId.set(
												transactionId,
												result.getTransaction,
											);
										})(),
									);
								},
							);

							await Promise.all(requests);

							// It is possible a cancel is handled while awaiting responses.
							if (done.isLocked) {
								return;
							}

							let approvedTransaction = undefined as
								| undefined
								| TransactionResponse;
							const transactions: Mutable<
								ADT.Pick<Page, "PaymentFlow">["transactions"]
							> = [];
							data.onVirtualTerminalRecordUpdate?.transactionRecords?.forEach(
								(transactionRecord) => {
									if (!transactionRecord) {
										return;
									}
									if (
										transactionRecord.status ===
										EcommerceTransactionStatus.PROCESSING
									) {
										return;
									}

									const transactionId = transactionRecord.transactionUuid;
									const transactionResponse =
										transactionResponses_byId.get(transactionId);
									if (!transactionResponse) {
										return;
									}

									transactions.push({
										id: transactionResponse.id,
										status:
											transactionResponse.status === TransactionStatus.APPROVED
												? "approved"
												: "declined",
										amount: transactionResponse.amount,
										currency: transactionResponse.currency ?? "AUD",
										timestamp: transactionResponse.timestamp,
										scheme: transactionResponse.scheme,
										maskedPan: transactionResponse.maskedPan ?? undefined,
									});

									if (
										transactionResponse.status === TransactionStatus.APPROVED
									) {
										approvedTransaction = transactionResponse;
									}
								},
							);

							if (approvedTransaction) {
								const _approvedTransaction = approvedTransaction as NonNullable<
									typeof approvedTransaction
								>;
								done(
									Page.PaymentFlow({
										state: ADT.value("Approved", {
											amount: approvedTransaction.amount,
											onDone: () => {
												if (!_approvedTransaction.externalReference) {
													logger.warn(
														"purchase: result external reference expected",
														{
															transactionId: _approvedTransaction.id,
															transactionReference:
																_approvedTransaction.reference,
															transactionExternalReference:
																_approvedTransaction.externalReference,
														},
													);
												}
												return void resolve(
													response_fromTransactionResponse(
														_approvedTransaction,
														{
															externalReference: request.query.reference,
															sessionUuid: request.query.sessionUuid,
														},
													),
												);
											},
										}),
										transactions,
										ecommerceReference: ecommerce.reference,
										viewedTimestamp: $viewedTimestamp,
									}),
								);
							} else if (isProcessing) {
								setPage(
									Page.PaymentFlow({
										state: ADT.value("Processing", {
											onCancel,
											timeoutDuration: expiryDuration_s,
										}),
										transactions,
										ecommerceReference: ecommerce.reference,
										viewedTimestamp: $viewedTimestamp,
									}),
								);
							} else {
								setPage(
									Page.PaymentFlow({
										state: ADT.value("Idle", {
											onCancel,
											timeoutDuration: expiryDuration_s,
										}),
										transactions,
										ecommerceReference: ecommerce.reference,
										viewedTimestamp: $viewedTimestamp,
									}),
								);
							}
						})().catch((error) => {
							logger.error("purchase: virtual terminal record update error", {
								error,
							});
							return;
						});
					};
				}
			}
		}

		return dispose;

		function setPage(nextOrFn: Page | ((prev: Page) => Page)) {
			return void _setPage((prev) => {
				const next = typeof nextOrFn === "function" ? nextOrFn(prev) : nextOrFn;
				logger.debug("purchase: navigation", {
					from: prev.$type,
					to: next.$type,
				});
				return next;
			});
		}
	}, [request, toaster, apiClient, wsClient, healthcheck]);

	return render(page);
}

PurchaseWithVirtualTerminal.onVirtualTerminalRecordUpdate = subscription(
	"onVirtualTerminalRecordUpdate",
	(q) => [
		q.onVirtualTerminalRecordUpdate(
			{
				entityUuid: $("entityId"),
				id: $("ecommerceId"),
			},
			(q) => [
				q.id,
				q.status,
				q.lastViewedAt,
				q.transactionRecords((q) => [q.transactionUuid, q.status]),
			],
		),
	],
);

PurchaseWithVirtualTerminal.getTransaction = query("getTransaction", (q) => [
	q.getTransaction(
		{
			deviceUuid: $("deviceId"),
			transactionUuid: $("transactionId"),
		},
		(q) => [
			q.id,
			q.status,
			q.amount,
			q.currency,
			q.timestamp,
			q.scheme,
			q.maskedPan,
			q.catid,
			q.caid,
			q.responseCode,
			q.responseDescription,
			q.cardMedia,
			q.cardholderUuid,
			q.isoProcessingCode,
			q.surchargeAmount,
			q.taxAmounts((q) => [q.amount]),
			q.tipAmount,
			q.reference,
			q.externalReference,
		],
	),
]);
